"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Input } from "@west-tennessee-consulting/ui";
import { Search } from "lucide-react";
import { buildFolderTree, rootTemplatesOf } from "../folder-tree.js";
import { TemplateBrowser } from "./TemplateBrowser.js";
import { TemplatePreviewPane } from "./TemplatePreviewPane.js";
/** Debounce a value; the picker searches server-side on the debounced term. */
export function useDebouncedValue(value, delayMs) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        if (Object.is(debounced, value))
            return;
        const t = setTimeout(() => setDebounced(value), delayMs);
        return () => clearTimeout(t);
        // `debounced` is intentionally read-only here — including it would reset the timer on settle.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, delayMs]);
    return { debounced, isDebouncing: !Object.is(debounced, value) };
}
/**
 * The shared body of WTC's ONE email-template picker (EMAIL_TEMPLATES_CANONICAL_SYSTEM):
 * search + tag filter + folder browser + preview pane. Purely presentational — the
 * host supplies data (Hub from `useEmailTemplates`, next-email-client from the
 * workspace-addon REST proxy) and the surrounding container/modal.
 */
export function TemplatePickerBody({ templates, folders, isLoading, searchValue, onSearchChange, isDebouncing = false, appliedSearchTerm, selectedTag = "", tagFilter, favoriteIds, isFavorite, onToggleFavorite, onApplyTemplate, resolveUnfilledVariables, wrapRow, }) {
    const [expandedFolders, setExpandedFolders] = useState(new Set());
    const [favoritesExpanded, setFavoritesExpanded] = useState(true);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const rootTemplates = useMemo(() => rootTemplatesOf(templates), [templates]);
    const folderTree = useMemo(() => buildFolderTree(folders, templates), [folders, templates]);
    const favoriteTemplates = useMemo(() => {
        const byId = new Map(templates.map((t) => [t.id, t]));
        const list = [];
        for (const id of favoriteIds) {
            const template = byId.get(id);
            if (template)
                list.push(template);
        }
        return list;
    }, [favoriteIds, templates]);
    const searchTerm = appliedSearchTerm ?? searchValue;
    const isSearching = searchTerm.trim().length > 0;
    const toggleFolder = useCallback((folderId) => {
        setExpandedFolders((prev) => {
            const next = new Set(prev);
            if (next.has(folderId))
                next.delete(folderId);
            else
                next.add(folderId);
            return next;
        });
    }, []);
    const handleApply = useCallback((template) => {
        onApplyTemplate(template);
        setSelectedTemplate(null);
    }, [onApplyTemplate]);
    const unfilled = useMemo(() => {
        if (!selectedTemplate || !resolveUnfilledVariables)
            return undefined;
        return resolveUnfilledVariables(selectedTemplate);
    }, [selectedTemplate, resolveUnfilledVariables]);
    return (_jsxs("div", { className: "space-y-4 flex-1 flex flex-col overflow-hidden w-full min-w-0", children: [_jsxs("div", { className: "flex flex-col md:flex-row gap-2", children: [_jsxs("div", { className: "relative flex-1", children: [_jsx(Search, { className: "absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" }), _jsx(Input, { placeholder: "Search templates by name, subject, or full template content...", value: searchValue, onChange: (e) => onSearchChange(e.target.value), className: "pl-10" })] }), tagFilter && _jsx("div", { className: "md:w-64", children: tagFilter })] }), _jsxs("div", { className: "grid grid-cols-1 lg:grid-cols-2 gap-4 flex-1 min-h-0", children: [_jsx(TemplateBrowser, { isLoading: isLoading, isDebouncing: isDebouncing, rootTemplates: rootTemplates, folderTree: folderTree, favoriteTemplates: favoriteTemplates, favoritesExpanded: favoritesExpanded, onFavoritesExpandedChange: setFavoritesExpanded, allTemplateCount: templates.length, isSearching: isSearching, searchTerm: searchTerm.trim(), selectedTag: selectedTag, selectedTemplateId: selectedTemplate?.id, expandedFolders: expandedFolders, onToggleFolder: toggleFolder, onPreviewTemplate: setSelectedTemplate, onApplyTemplate: handleApply, isFavorite: isFavorite, onToggleFavorite: onToggleFavorite, wrapRow: wrapRow }), _jsx(TemplatePreviewPane, { selectedTemplate: selectedTemplate, isFavorite: selectedTemplate ? isFavorite(selectedTemplate.id) : false, onToggleFavorite: onToggleFavorite, onApplyTemplate: handleApply, unfilledVariables: unfilled })] })] }));
}
